import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Copy, Loader2, ShieldCheck, ShieldOff } from 'lucide-react';

import { twoFactorService, type TwoFactorMethodKind } from '../../services/twoFactorService';

import { LearnerChannelTwoFactorPanel } from './LearnerChannelTwoFactorPanel';

import {

  methodLabel,

  TwoFactorMethodTabs,

  type SelectableTwoFactorMethod

} from './TwoFactorMethodTabs';

import { useNotifications } from '../feedback/NotificationSystem';

import { useAppLocale } from '../../providers/LocaleProvider';

import type { LearnerSettingsSectionTheme } from './learnerSettingsSectionThemes';



interface LearnerTwoFactorSectionProps {

  theme: LearnerSettingsSectionTheme;

  sectionPad: string;

  isModal: boolean;

  /** Simple-look tour: the opened section title, not the closed list row. */
  headingTourId?: string;

}



/** Avoid browser/password-manager autofill of the login password — user must type intentionally. */

const ManualPasswordInput: React.FC<{

  id: string;

  value: string;

  onChange: (value: string) => void;

  placeholder?: string;

  className?: string;

}> = ({ id, value, onChange, placeholder = 'Password', className }) => {

  const [allowEdit, setAllowEdit] = useState(false);



  return (

    <input

      id={id}

      name={id}

      type="password"

      autoComplete="off"

      autoCorrect="off"

      autoCapitalize="off"

      spellCheck={false}

      data-lpignore="true"

      data-1p-ignore="true"

      data-form-type="other"

      placeholder={placeholder}

      value={value}

      readOnly={!allowEdit}

      onFocus={() => setAllowEdit(true)}

      onChange={(e) => onChange(e.target.value)}

      className={className}

    />

  );

};



function defaultViewMethod(enabled: boolean, activeMethod: TwoFactorMethodKind): SelectableTwoFactorMethod {

  if (enabled && activeMethod !== 'none') {

    if (activeMethod === 'email' || activeMethod === 'telegram' || activeMethod === 'bale') {

      return activeMethod;

    }

    if (activeMethod === 'authenticator') {

      return 'authenticator';

    }

  }

  return 'authenticator';

}



export const LearnerTwoFactorSection: React.FC<LearnerTwoFactorSectionProps> = ({

  theme,

  sectionPad,

  isModal,

  headingTourId

}) => {
  const { t } = useTranslation('settings');
  const { direction } = useAppLocale();
  const { showSuccess, showError } = useNotifications();

  const [loading, setLoading] = useState(true);

  const [enabled, setEnabled] = useState(false);

  const [hasAuthenticator, setHasAuthenticator] = useState(false);

  const [hasPassword, setHasPassword] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethodKind>('none');

  const [viewMethod, setViewMethod] = useState<SelectableTwoFactorMethod>('authenticator');

  const [telegramLinked, setTelegramLinked] = useState(false);

  const [baleLinked, setBaleLinked] = useState(false);

  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [profilePhoneNumber, setProfilePhoneNumber] = useState<string | null>(null);
  const [twoFactorChannelPhone, setTwoFactorChannelPhone] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState<string | null>(null);

  const [telegramBotUsername, setTelegramBotUsername] = useState<string | null>(null);

  const [baleBotUsername, setBaleBotUsername] = useState<string | null>(null);

  const [emailChannelAvailable, setEmailChannelAvailable] = useState(true);

  const prevShowDisablePanelRef = useRef(false);

  const [recoveryLeft, setRecoveryLeft] = useState(0);



  const [setupUri, setSetupUri] = useState('');

  const [sharedKey, setSharedKey] = useState('');

  const [verifyCode, setVerifyCode] = useState('');

  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const [disablePassword, setDisablePassword] = useState('');

  const [disableCode, setDisableCode] = useState('');

  const [busy, setBusy] = useState(false);

  const [enrolling, setEnrolling] = useState(false);

  const [enrollMode, setEnrollMode] = useState<'new' | 'rescan' | 'replace'>('new');

  const [reenrolling, setReenrolling] = useState(false);

  const [awaitingSetupPassword, setAwaitingSetupPassword] = useState(false);

  const [setupPassword, setSetupPassword] = useState('');

  const [pendingSetupReset, setPendingSetupReset] = useState(false);



  const applyStatus = useCallback(

    (s: Awaited<ReturnType<typeof twoFactorService.getStatus>>) => {

      setEnabled(s.twoFactorEnabled);

      setHasAuthenticator(s.hasAuthenticator);

      setRecoveryLeft(s.recoveryCodesLeft);

      setHasPassword(s.hasPassword);

      setSelectedMethod(s.selectedTwoFactorMethod);

      setTelegramLinked(s.telegramLinked);

      setBaleLinked(s.baleLinked);

      setMaskedPhone(s.maskedChannelPhone ?? null);
      setProfilePhoneNumber(s.phoneNumber ?? null);
      setTwoFactorChannelPhone(s.twoFactorChannelPhone ?? null);
      setAccountEmail(s.accountEmail ?? null);

      setTelegramBotUsername(s.telegramBotUsername ?? null);

      setBaleBotUsername(s.baleBotUsername ?? null);

      setEmailChannelAvailable(s.emailChannelAvailable);

      setViewMethod((prev) => {

        if (s.twoFactorEnabled && s.selectedTwoFactorMethod !== 'none') {

          return defaultViewMethod(true, s.selectedTwoFactorMethod);

        }

        return prev;

      });

    },

    []

  );



  const resetInteractionState = useCallback(() => {

    setEnrolling(false);

    setReenrolling(false);

    setAwaitingSetupPassword(false);

    setSetupPassword('');

    setPendingSetupReset(false);

    setVerifyCode('');

    setSetupUri('');

    setSharedKey('');

    setDisablePassword('');

    setDisableCode('');

  }, []);



  const loadStatus = useCallback(

    async (options?: { silent?: boolean }) => {

      if (!options?.silent) {

        setLoading(true);

      }

      try {

        const s = await twoFactorService.getStatus();

        applyStatus(s);

      } catch (e) {

        showError(e instanceof Error ? e.message : t('security.loadFailed'));

      } finally {

        if (!options?.silent) {

          setLoading(false);

        }

      }

    },

    [applyStatus, showError]

  );



  useEffect(() => {

    void loadStatus();

  }, [loadStatus]);



  const loadSetupQr = async (reset: boolean, password?: string | null) => {

    setBusy(true);

    try {

      const setup = await twoFactorService.setup(reset, password);

      setSetupUri(setup.authenticatorUri);

      setSharedKey(setup.sharedKey);

      setEnrollMode(reset ? 'replace' : hasAuthenticator ? 'rescan' : 'new');

      setEnrolling(true);

      setReenrolling(false);

      setAwaitingSetupPassword(false);

      setSetupPassword('');

    } catch (e) {

      showError(e instanceof Error ? e.message : t('security.qrLoadFailed'));

    } finally {

      setBusy(false);

    }

  };



  const requestShowQr = (reset: boolean) => {

    setRecoveryCodes(null);

    setVerifyCode('');

    setReenrolling(false);

    if (enabled && hasPassword) {

      setSetupPassword('');

      setPendingSetupReset(reset);

      setAwaitingSetupPassword(true);

      return;

    }

    void loadSetupQr(reset, null);

  };



  const startNewEnroll = () => {

    resetInteractionState();

    setRecoveryCodes(null);

    void loadSetupQr(false, null);

  };



  const startReenroll = () => {

    setRecoveryCodes(null);

    resetInteractionState();

    setReenrolling(true);

  };



  const confirmEnable = async () => {

    if (verifyCode.replace(/\s/g, '').length < 6) {

      showError(t('security.enterAuthenticatorCode'));

      return;

    }

    setBusy(true);

    try {

      const r = await twoFactorService.enable(verifyCode);

      if (!r.success) {

        showError(r.message || t('security.invalidCode'));

        return;

      }

      resetInteractionState();

      setEnrolling(false);

      setReenrolling(false);

      setEnabled(true);

      setHasAuthenticator(true);

      setSelectedMethod('authenticator');

      if (r.recoveryCodes?.length) {

        setRecoveryCodes(r.recoveryCodes);

        setRecoveryLeft(r.recoveryCodes.length);

      }

      showSuccess(
        enabled
          ? t('security.authenticatorUpdated')
          : reenrolling
            ? t('security.twoFactorOnAgain')
            : t('security.twoFactorEnabled')
      );

      await loadStatus({ silent: true });

    } catch (e) {

      showError(e instanceof Error ? e.message : t('security.enableFailed'));

    } finally {

      setBusy(false);

    }

  };



  const handleDisable = async () => {

    if (disableCode.replace(/\s/g, '').length < 6) {

      showError(t('security.enterDisableCode'));

      return;

    }

    if (hasPassword && !disablePassword.trim()) {

      showError(t('security.enterAccountPassword'));

      return;

    }

    setBusy(true);

    try {

      const r = await twoFactorService.disable(

        disableCode,

        hasPassword ? disablePassword : null

      );

      if (!r.success) {

        showError(r.message || t('security.disableFailed'));

        return;

      }

      resetInteractionState();

      setRecoveryCodes(null);

      setEnabled(false);

      setEnrolling(false);

      setReenrolling(false);

      showSuccess(t('security.twoFactorDisabled'));

      await loadStatus({ silent: true });

    } catch (e) {

      showError(e instanceof Error ? e.message : t('security.disableFailed'));

    } finally {

      setBusy(false);

    }

  };



  const copyKey = async () => {

    try {

      await navigator.clipboard.writeText(sharedKey.replace(/\s/g, ''));

      showSuccess(t('security.setupKeyCopied'));

    } catch {

      showError(t('security.copyFailed'));

    }

  };



  const qrSrc = setupUri

    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupUri)}`

    : '';



  const authenticatorActive = enabled && selectedMethod === 'authenticator';

  const channelMethodActive =

    enabled && (selectedMethod === 'email' || selectedMethod === 'telegram' || selectedMethod === 'bale');

  const anotherMethodActiveForAuthenticator =

    enabled && selectedMethod !== 'authenticator' && selectedMethod !== 'none';



  const showDisablePanel = authenticatorActive && !enrolling && !reenrolling && !awaitingSetupPassword;

  const showReenablePanel = !enabled && reenrolling && !enrolling;

  const showNewEnrollPanel = enrolling;

  const showStartButtons =

    !enabled && !channelMethodActive && !enrolling && !reenrolling && !awaitingSetupPassword;

  const showQrUnlockPanel = awaitingSetupPassword;



  useEffect(() => {

    if (showDisablePanel && !prevShowDisablePanelRef.current) {

      setDisablePassword('');

    }

    prevShowDisablePanelRef.current = showDisablePanel;

  }, [showDisablePanel]);



  const handleViewMethodChange = (method: SelectableTwoFactorMethod) => {

    setViewMethod(method);

    if (method !== 'authenticator') {

      resetInteractionState();

    }

  };



  return (

    <section

      className={

        isModal

          ? `${theme.section} ${sectionPad}`

          : `rounded-2xl border border-slate-300/90 bg-gradient-to-br from-white via-emerald-50/40 to-slate-50/90 shadow-md shadow-slate-300/50 dark:border-gray-600/90 dark:from-gray-800 dark:via-emerald-950/25 dark:to-gray-900 dark:shadow-black/30 overflow-hidden ${sectionPad}`

      }

    >

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">

        <div

          className={

            isModal

              ? theme.icon

              : 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30'

          }

        >

          <ShieldCheck className="h-6 w-6" aria-hidden />

        </div>

        <div className={`min-w-0 flex-1 ${isModal ? 'text-start' : ''}`} dir={isModal ? direction : undefined}>

          <h2
            className="mb-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-white"
            {...(headingTourId ? { 'data-tour-id': headingTourId } : {})}
          >

            {t('security.title')}

          </h2>

          <p className="mb-4 max-w-prose text-xs text-slate-500 dark:text-slate-400">

            {t('security.intro')}

            {!hasPassword && ` ${t('security.googleOnlyHint')}`}

          </p>



          {loading ? (

            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">

              <Loader2 className={`h-5 w-5 animate-spin ${theme.loader}`} />

              {t('security.loading')}

            </div>

          ) : (

            <>

              <p className="mb-3 text-sm font-medium text-slate-800 dark:text-slate-100">

                {t('security.statusLabel')}{' '}

                <span className={enabled ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}>

                  {enabled ? t('security.statusOn') : t('security.statusOff')}

                </span>

                {enabled && selectedMethod !== 'none' && (

                  <span className="ms-2 text-xs font-normal text-slate-500">

                    ({methodLabel(selectedMethod, t)})

                  </span>

                )}

                {authenticatorActive && recoveryLeft > 0 && (

                  <span className="ms-2 text-xs font-normal text-slate-500">

                    ({t('security.recoveryCodesLeft', { count: recoveryLeft })})

                  </span>

                )}

              </p>



              <div className="mb-4 space-y-4">

                <TwoFactorMethodTabs

                  selected={viewMethod}

                  onSelect={handleViewMethodChange}

                  emailAvailable={emailChannelAvailable}

                  enabled={enabled}

                  activeMethod={selectedMethod}

                />



                <div data-tour-id="quick-settings-security-setup" className="space-y-4">

                {viewMethod === 'authenticator' && (

                  <div className="space-y-3">

                    {anotherMethodActiveForAuthenticator ? (

                      <div className="rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                        <p className="text-xs text-slate-600 dark:text-slate-300">

                          {t('security.anotherMethodActiveAuthenticator', {
                            method: methodLabel(selectedMethod, t),
                          })}

                        </p>

                      </div>

                    ) : (

                      <>

                        {recoveryCodes && recoveryCodes.length > 0 && (

                          <div className="rounded-xl border border-amber-300/80 bg-amber-50/90 p-3 dark:border-amber-800/60 dark:bg-amber-950/30">

                            <p className="mb-2 text-xs font-semibold text-amber-900 dark:text-amber-100">

                              {t('security.recoveryCodesSaveNow')}

                            </p>

                            <ul className="grid gap-1 font-mono text-xs text-amber-950 dark:text-amber-50 sm:grid-cols-2">

                              {recoveryCodes.map((c) => (

                                <li key={c}>{c}</li>

                              ))}

                            </ul>

                          </div>

                        )}



                        {showStartButtons && (

                          <div className="space-y-3 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                            {hasAuthenticator ? (

                              <>

                                <p className="text-[11px] text-slate-600 dark:text-slate-400">

                                  <Trans i18nKey="security.reenableAuthenticatorHint" ns="settings" components={{ strong: <strong /> }} />

                                </p>

                                <div className="flex flex-wrap gap-2">

                                  <button type="button" onClick={startReenroll} disabled={busy} className={theme.saveButton}>

                                    {t('security.reEnable2fa')}

                                  </button>

                                  <button

                                    type="button"

                                    onClick={() => requestShowQr(false)}

                                    disabled={busy}

                                    className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 dark:border-gray-600 dark:text-slate-100 dark:hover:bg-gray-800"

                                  >

                                    {t('security.showQrAgain')}

                                  </button>

                                </div>

                              </>

                            ) : (

                              <>

                                <p className="text-[11px] text-slate-600 dark:text-slate-400">

                                  {t('security.setupStep1')}

                                  <br />

                                  {t('security.setupStep2')}

                                  <br />

                                  {t('security.setupStep3')}

                                </p>

                                <button

                                  type="button"

                                  onClick={() => startNewEnroll()}

                                  disabled={busy}

                                  className={theme.saveButton}

                                >

                                  {busy ? <Loader2 className="inline h-4 w-4 animate-spin" /> : t('security.setupAuthenticator')}

                                </button>

                              </>

                            )}

                          </div>

                        )}



                        {showQrUnlockPanel && (

                          <div className="space-y-3 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                            <p className="text-xs text-slate-600 dark:text-slate-300">

                              {t('security.qrPasswordPrompt')}

                            </p>

                            <ManualPasswordInput

                              id="learner-2fa-setup-password"

                              value={setupPassword}

                              onChange={setSetupPassword}

                              className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"

                            />

                            <div className="flex flex-wrap gap-2">

                              <button

                                type="button"

                                onClick={() => void loadSetupQr(pendingSetupReset, setupPassword)}

                                disabled={busy || !setupPassword.trim()}

                                className={theme.saveButton}

                              >

                                {t('security.showQr')}

                              </button>

                              <button

                                type="button"

                                onClick={() => setAwaitingSetupPassword(false)}

                                className="rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-gray-600"

                              >

                                {t('security.cancel')}

                              </button>

                            </div>

                          </div>

                        )}



                        {showReenablePanel && (

                          <div className="space-y-4 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                            <p className="text-xs text-slate-600 dark:text-slate-300">

                              <Trans i18nKey="security.reenablePanelHint" ns="settings" components={{ strong: <strong /> }} />

                            </p>

                            <button

                              type="button"

                              onClick={() => requestShowQr(false)}

                              disabled={busy}

                              className="text-xs font-medium text-teal-700 underline hover:text-teal-900 dark:text-teal-400"

                            >

                              Show QR code again

                            </button>

                            <input

                              type="text"

                              inputMode="numeric"

                              autoComplete="one-time-code"

                              maxLength={8}

                              placeholder={t('security.sixDigitCodePlaceholder')}

                              value={verifyCode}

                              onChange={(e) => setVerifyCode(e.target.value.replace(/[^\d\s-]/g, ''))}

                              className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"

                            />

                            <div className="flex flex-wrap gap-2">

                              <button type="button" onClick={() => void confirmEnable()} disabled={busy} className={theme.saveButton}>

                                {busy ? t('security.verifying') : t('security.turnOn2fa')}

                              </button>

                              <button

                                type="button"

                                onClick={() => setReenrolling(false)}

                                className="rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-gray-600"

                              >

                                {t('security.cancel')}

                              </button>

                            </div>

                          </div>

                        )}



                        {showNewEnrollPanel && (

                          <div className="space-y-4 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                            <p className="text-xs text-slate-600 dark:text-slate-300">

                              {enrollMode === 'rescan' ? (

                                t(enabled ? 'security.rescanQrConfirm' : 'security.rescanQrReenable')

                              ) : enrollMode === 'replace' ? (

                                <Trans i18nKey="security.replaceQrHint" ns="settings" components={{ strong: <strong /> }} />

                              ) : (

                                <>

                                  {t('security.setupStep1')}

                                  <br />

                                  {t('security.setupStep2Manual')}

                                  <br />

                                  {t('security.setupStep3')}

                                </>

                              )}

                            </p>

                            {qrSrc && (

                              <img

                                src={qrSrc}

                                width={180}

                                height={180}

                                alt={t('security.qrAlt')}

                                className="mx-auto rounded-lg border border-slate-200 bg-white p-2 dark:border-gray-600"

                              />

                            )}

                            {sharedKey && (

                              <div className="flex flex-wrap items-center gap-2">

                                <code className="break-all rounded bg-slate-100 px-2 py-1 text-xs dark:bg-gray-800">

                                  {sharedKey}

                                </code>

                                <button

                                  type="button"

                                  onClick={() => void copyKey()}

                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 dark:border-gray-600 dark:hover:bg-gray-800"

                                >

                                  <Copy className="h-3.5 w-3.5" />

                                  {t('security.copyKey')}

                                </button>

                              </div>

                            )}

                            <input

                              type="text"

                              inputMode="numeric"

                              autoComplete="one-time-code"

                              maxLength={8}

                              placeholder={t('security.sixDigitCodePlaceholder')}

                              value={verifyCode}

                              onChange={(e) => setVerifyCode(e.target.value.replace(/[^\d\s-]/g, ''))}

                              className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"

                            />

                            <div className="flex flex-wrap gap-2">

                              <button type="button" onClick={() => void confirmEnable()} disabled={busy} className={theme.saveButton}>

                                {busy ? t('security.verifying') : enabled ? t('security.confirm') : t('security.enable2fa')}

                              </button>

                              <button

                                type="button"

                                onClick={() => setEnrolling(false)}

                                className="rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-gray-600"

                              >

                                {t('security.cancel')}

                              </button>

                            </div>

                            {hasAuthenticator && enrollMode !== 'replace' && (

                              <button

                                type="button"

                                onClick={() => requestShowQr(true)}

                                disabled={busy}

                                className="text-xs text-slate-500 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"

                              >

                                {t('security.generateNewAuthenticator')}

                              </button>

                            )}

                          </div>

                        )}



                        {showDisablePanel && (

                          <div className="space-y-3 rounded-xl border border-slate-200/90 bg-white/60 p-4 dark:border-gray-600 dark:bg-gray-900/40">

                            <p className="text-xs text-slate-600 dark:text-slate-300">

                              {t('security.authenticatorActive')}

                            </p>

                            <p className="text-[11px] text-slate-500 dark:text-slate-400">

                              {hasPassword ? t('security.disableWithPassword') : t('security.disableCodeOnly')}

                            </p>

                            {hasPassword && (

                              <ManualPasswordInput

                                id="learner-2fa-disable-password"

                                value={disablePassword}

                                onChange={setDisablePassword}

                                className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"

                              />

                            )}

                            <input

                              type="text"

                              inputMode="numeric"

                              autoComplete="one-time-code"

                              placeholder={t('security.disableCodePlaceholder')}

                              value={disableCode}

                              onChange={(e) => setDisableCode(e.target.value)}

                              className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"

                            />

                            <button

                              type="button"

                              onClick={() => void handleDisable()}

                              disabled={busy}

                              className="inline-flex items-center gap-2 rounded-xl border border-red-300/80 bg-red-50 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/60"

                            >

                              <ShieldOff className="h-4 w-4" />

                              {busy ? t('security.disabling') : t('security.turnOff2fa')}

                            </button>

                            {hasAuthenticator && (

                              <button

                                type="button"

                                onClick={() => requestShowQr(false)}

                                disabled={busy}

                                className="block text-xs text-teal-700 underline hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300"

                              >

                                {t('security.removedFromAppShowQr')}

                              </button>

                            )}

                          </div>

                        )}

                      </>

                    )}

                  </div>

                )}



                {viewMethod === 'email' && emailChannelAvailable && (

                  <LearnerChannelTwoFactorPanel

                    theme={theme}

                    method="email"

                    enabled={enabled}

                    activeMethod={selectedMethod}

                    telegramLinked={telegramLinked}

                    baleLinked={baleLinked}

                    maskedPhone={maskedPhone}

                    profilePhoneNumber={profilePhoneNumber}

                    twoFactorChannelPhone={twoFactorChannelPhone}

                    accountEmail={accountEmail}

                    telegramBotUsername={telegramBotUsername}

                    baleBotUsername={baleBotUsername}

                    onStatusChanged={() => void loadStatus({ silent: true })}

                  />

                )}



                {viewMethod === 'telegram' && (

                  <LearnerChannelTwoFactorPanel

                    theme={theme}

                    method="telegram"

                    enabled={enabled}

                    activeMethod={selectedMethod}

                    telegramLinked={telegramLinked}

                    baleLinked={baleLinked}

                    maskedPhone={maskedPhone}

                    profilePhoneNumber={profilePhoneNumber}

                    twoFactorChannelPhone={twoFactorChannelPhone}

                    accountEmail={accountEmail}

                    telegramBotUsername={telegramBotUsername}

                    baleBotUsername={baleBotUsername}

                    onStatusChanged={() => void loadStatus({ silent: true })}

                  />

                )}



                {viewMethod === 'bale' && (

                  <LearnerChannelTwoFactorPanel

                    theme={theme}

                    method="bale"

                    enabled={enabled}

                    activeMethod={selectedMethod}

                    telegramLinked={telegramLinked}

                    baleLinked={baleLinked}

                    maskedPhone={maskedPhone}

                    profilePhoneNumber={profilePhoneNumber}

                    twoFactorChannelPhone={twoFactorChannelPhone}

                    accountEmail={accountEmail}

                    telegramBotUsername={telegramBotUsername}

                    baleBotUsername={baleBotUsername}

                    onStatusChanged={() => void loadStatus({ silent: true })}

                  />

                )}

                </div>

              </div>

            </>

          )}

        </div>

      </div>

    </section>

  );

};

